import { Slider } from "@heroui/react";
import { AnimatePresence, m, useReducedMotion } from "framer-motion";
import { memo, useRef, useState, type ReactNode } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

export type SliderGroupItem = {
  id: string;
  /** Label rendered on the left edge of the track (e.g. "Gentle"). */
  leftLabel: string;
  /** Label rendered on the right edge of the track (e.g. "Direct"). */
  rightLabel: string;
  /** Initial position; values are 0–100 unless `min`/`max` are overridden. */
  defaultValue: number;
  min?: number;
  max?: number;
  step?: number;
  /**
   * Optional dynamic summary label rendered below the track. Re-evaluated on
   * every value change and crossfaded between renders. Use this to surface
   * the user's current personality position in plain language, e.g.
   * `(v) => v < 35 ? "Slightly gentle" : v < 65 ? "Balanced" : "A little direct"`.
   */
  formatSummary?: (value: number) => string;
};

export type SliderGroupProps = {
  question: string;
  description?: string;
  sliders: SliderGroupItem[];
  /** Map of slider id → initial value. Overrides each slider's `defaultValue`. */
  initialValues?: Record<string, number>;
  /** Fired on every slider change with the full id → value map. */
  onChange?: (values: Record<string, number>) => void;
  /** Fired the first time the user moves a particular slider. */
  onTouch?: (id: string) => void;
  className?: string;
  testId?: string;
};

const buildInitialState = (
  sliders: SliderGroupItem[],
  initialValues: Record<string, number> | undefined,
): Record<string, number> => {
  const next: Record<string, number> = {};
  for (const slider of sliders) {
    next[slider.id] = initialValues?.[slider.id] ?? slider.defaultValue;
  }
  return next;
};

const SummaryLabel = ({
  text,
  prefersReduced,
}: {
  text: string;
  prefersReduced: boolean;
}): ReactNode => {
  if (prefersReduced) {
    return (
      <span
        className="text-xs font-medium tabular-nums"
        style={{ color: "hsl(var(--brand-primary))" }}
      >
        {text}
      </span>
    );
  }
  return (
    <span
      className="relative inline-block min-h-[1.1em] text-xs font-medium tabular-nums"
      style={{ color: "hsl(var(--brand-primary))" }}
    >
      <AnimatePresence initial={false} mode="popLayout">
        <m.span
          key={text}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.18, ease: [0.32, 0.72, 0, 1] }}
        >
          {text}
        </m.span>
      </AnimatePresence>
    </span>
  );
};

type SliderRowProps = {
  slider: SliderGroupItem;
  value: number;
  onValueChange: (value: number) => void;
  prefersReduced: boolean;
  testId?: string;
};

const SliderRow = ({ slider, value, onValueChange, prefersReduced, testId }: SliderRowProps) => {
  const min = slider.min ?? 0;
  const max = slider.max ?? 100;
  const summary = slider.formatSummary?.(value);

  const handleChange = (next: number | number[]): void => {
    const v = Array.isArray(next) ? (next[0] ?? min) : next;
    onValueChange(v);
  };

  return (
    <div
      data-testid={testId ? `${testId}-row-${slider.id}` : undefined}
      className="flex flex-col gap-2"
    >
      <div
        className="flex items-baseline justify-between text-xs font-medium"
        style={{ color: "hsl(var(--hero-fg-muted))" }}
      >
        <span>{slider.leftLabel}</span>
        <span>{slider.rightLabel}</span>
      </div>
      <Slider
        aria-label={`${slider.leftLabel} to ${slider.rightLabel}`}
        minValue={min}
        maxValue={max}
        step={slider.step ?? 1}
        value={value}
        onChange={handleChange}
        className="w-full"
      >
        <Slider.Track>
          <Slider.Fill />
          <Slider.Thumb />
        </Slider.Track>
      </Slider>
      {summary !== undefined && (
        <div className="min-h-[1.1em]">
          <SummaryLabel text={summary} prefersReduced={prefersReduced} />
        </div>
      )}
    </div>
  );
};

const SliderGroupImpl = ({
  question,
  description,
  sliders,
  initialValues,
  onChange,
  onTouch,
  className,
  testId,
}: SliderGroupProps) => {
  const prefersReduced = useReducedMotion();
  const [values, setValues] = useState<Record<string, number>>(() =>
    buildInitialState(sliders, initialValues),
  );
  // Ref-based one-shot guard for onTouch: a slider fires onTouch only the
  // first time the user interacts with it. Using a ref (not state) keeps this
  // bookkeeping out of the render cycle — state updater functions must be pure
  // and must not call external setState (e.g. onAnswer → Funnel setState).
  const touchedRef = useRef<Record<string, boolean>>({});

  const handleSliderChange = (id: string, next: number): void => {
    if (values[id] === next) return;
    const merged = { ...values, [id]: next };
    setValues(merged);
    onChange?.(merged);
    if (!touchedRef.current[id]) {
      touchedRef.current[id] = true;
      onTouch?.(id);
    }
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      <div className="flex flex-col gap-5">
        {sliders.map((slider) => (
          <SliderRow
            key={slider.id}
            slider={slider}
            value={values[slider.id] ?? slider.defaultValue}
            onValueChange={(next) => handleSliderChange(slider.id, next)}
            prefersReduced={!!prefersReduced}
            testId={testId}
          />
        ))}
      </div>
    </section>
  );
};

export const SliderGroup = memo(SliderGroupImpl);

export default SliderGroup;
